"use client";

import { useAuth, useClerk } from "@clerk/nextjs";
import { useEffect, useRef, useCallback } from "react";
import { useAuth as useAuthStore } from "@/stores/use-auth-store";

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000; // 1 second

export function useClerkAuth() {
  const { getToken, userId, isLoaded } = useAuth();
  const { signOut } = useClerk();
  const { setToken, setUserId } = useAuthStore();
  const retryCountRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const syncAuth = useCallback(async (isRetry = false) => {
    if (!isLoaded) return;

    try {
      if (userId) {
        // Get fresh token without template (or use template if configured in Clerk dashboard)
        // Remove template parameter if "default" template doesn't exist in your Clerk dashboard
        const token = await getToken();

        if (token) {
          setToken(token);
          setUserId(userId);
          retryCountRef.current = 0; // Reset retry count on success
        } else {
          // Session exists but token is missing/expired — clear state and sign out.
          console.warn("Token is null for authenticated user");
          setToken(null);
          setUserId(null);
          await signOut({ redirectUrl: "/sign-in" });
        }
      } else {
        // User is not authenticated
        setToken(null);
        setUserId(null);
        retryCountRef.current = 0;
      }
    } catch (error) {
      console.error("Error syncing Clerk auth:", error);
      
      // Retry logic for transient errors
      if (retryCountRef.current < MAX_RETRY_ATTEMPTS && !isRetry) {
        retryCountRef.current += 1;
        
        setTimeout(() => {
          syncAuth(true);
        }, RETRY_DELAY * retryCountRef.current);
      } else {
        // Max retries reached or already retrying
        setToken(null);
        setUserId(null);
        if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
          console.error("Max retry attempts reached for auth sync");
          retryCountRef.current = 0;
        }
      }
    }
  }, [getToken, userId, isLoaded, setToken, setUserId, signOut]);

  useEffect(() => {
    if (!isLoaded) return;

    // Initial sync
    syncAuth();

    // Set up periodic refresh
    intervalRef.current = setInterval(() => {
      syncAuth();
    }, REFRESH_INTERVAL);

    // Cleanup on unmount or dependency change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [syncAuth, isLoaded]);

  return { isLoaded };
}

